import {
  faExclamationCircle,
  faInfoCircle,
  faTimesCircle,
  type IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ComponentPropsWithoutRef, FC, ReactNode } from 'react';
import { Heading } from 'src/components/text/heading';
import { useIsNewDesignActive } from 'src/pages/new-design/is-new-design';
import { cn } from 'src/utils/cn';

type AlertProps = {
  type: 'info' | 'warning' | 'error';
  title?: ReactNode;
} & ComponentPropsWithoutRef<'div'>;

const label = {
  warning: 'Warning',
  error: 'Error',
  info: 'Info',
};

const LegacyAlert: FC<AlertProps> = ({ type, children, title, ...rest }) => {
  return (
    <div
      className={cn(
        `p-[1.5rem]`,
        type === 'info' && `bg-legacy-brand-light-4 text-legacy-brand`,
        type === 'warning' && `bg-[#fcf8e3] text-[#8a6d3b]`,
        type === 'error' && `bg-legacy-error-lighter text-legacy-error`
      )}
      {...rest}
    >
      {title ? (
        <div
          className={`
            grid items-center gap-md
            legacy:gap-[1rem]
          `}
        >
          <Heading mode="title" size="md">
            <FontAwesomeIcon
              icon={type === 'info' ? faInfoCircle : faExclamationCircle}
              aria-hidden
            />{' '}
            {title}
          </Heading>
          <div>{children}</div>
        </div>
      ) : (
        <>
          <FontAwesomeIcon
            icon={type === 'info' ? faInfoCircle : faExclamationCircle}
            aria-hidden
          />{' '}
          <strong>{label[type]}</strong> {children}
        </>
      )}
    </div>
  );
};

const icon: Record<AlertProps['type'], IconDefinition> = {
  info: faInfoCircle,
  warning: faExclamationCircle,
  error: faTimesCircle,
} as const;

const classNames: Record<AlertProps['type'], string> = {
  info: cn(`bg-background-support-info-minimal text-foreground-support-info`),
  warning: cn(
    `bg-background-support-warning-minimal text-foreground-support-warning`
  ),
  error: cn(
    `bg-background-support-error-minimal text-foreground-support-error`
  ),
} as const;

const NewAlert: FC<AlertProps> = ({ type, children, title, ...rest }) => {
  return (
    <div
      className={cn(
        classNames[type],
        `grid grid-cols-[auto_1fr] gap-lg rounded-xs px-xl py-lg`
      )}
      {...rest}
    >
      <FontAwesomeIcon icon={icon[type]} size="lg" aria-hidden />

      <div className={cn(`grid gap-sm`)} {...rest}>
        {title && (
          <Heading mode="title" size="sm" className={cn(`text-inherit!`)}>
            {title}
          </Heading>
        )}

        <div>{children}</div>
      </div>
    </div>
  );
};

export const Alert: FC<AlertProps> = (props) => {
  const isNewDesignActive = useIsNewDesignActive();

  return isNewDesignActive ? (
    <NewAlert {...props} />
  ) : (
    <LegacyAlert {...props} />
  );
};
